import type { MobilityProviderId, ProviderPosition, ProviderVariant } from '../types'

const EXPERIMENT_STORAGE_KEY = 'kvibe_provider_poc_experiment_v1'

export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export interface ProviderExperiment {
  sessionId: string
  providerVariant: ProviderVariant
}

function isProviderExperiment(value: unknown): value is ProviderExperiment {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<ProviderExperiment>
  return typeof candidate.sessionId === 'string'
    && candidate.sessionId.length >= 8
    && (candidate.providerVariant === 'A' || candidate.providerVariant === 'B')
}

function createSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `poc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

export function getOrCreateProviderExperiment(
  storage: StorageLike | null,
  random: () => number = Math.random,
): ProviderExperiment {
  if (storage) {
    try {
      const raw = storage.getItem(EXPERIMENT_STORAGE_KEY)
      const parsed = raw ? JSON.parse(raw) as unknown : null
      if (isProviderExperiment(parsed)) return parsed
    } catch {
      // A new in-memory session is used when sessionStorage is unavailable/corrupt.
    }
  }

  const experiment: ProviderExperiment = {
    sessionId: createSessionId(),
    providerVariant: random() < 0.5 ? 'A' : 'B',
  }
  try {
    storage?.setItem(EXPERIMENT_STORAGE_KEY, JSON.stringify(experiment))
  } catch {
    // The experiment remains stable for the current React mount.
  }
  return experiment
}

export function providerOrderForVariant(variant: ProviderVariant): MobilityProviderId[] {
  return variant === 'A' ? ['kride', 'uber'] : ['uber', 'kride']
}

export function providerPositionsForVariant(variant: ProviderVariant): Record<MobilityProviderId, ProviderPosition> {
  const [top, bottom] = providerOrderForVariant(variant)
  return { [top]: 'top', [bottom]: 'bottom' } as Record<MobilityProviderId, ProviderPosition>
}
